/**
 * DemoMarketDataProvider — التنفيذ الوحيد لـMarketDataProvider في PHASE 1.
 *
 * كل رقم يخرج من هنا **تجريبي ومولّد حتمياً**. لا يوجد أي محرّك تقييم حقيقي:
 * لا Fair Value، ولا Comparable Selection، ولا Opportunity، ولا Confidence،
 * ولا Market Pressure. القيم مأخوذة مباشرة من طبقة بيانات العرض المعتمدة في
 * النموذج، وتُعاد بنفس شكل العقد الذي سينتجه ProductionMarketDataProvider.
 *
 * الغرض من ذلك: تثبيت الشكل (Contract) قبل بناء المحرّك، حتى لا تُعاد كتابة
 * الواجهة عند استبدال هذه الطبقة.
 */

import {
  ProviderError,
  type GetDistrictParams,
  type GetMapMetricsParams,
  type GetOpportunitiesParams,
  type MarketDataProvider,
  type WatchlistMutation,
} from '@/domain/market-data-provider';
import { MODEL_VERSIONS } from '@/domain/versions';
// دلالات موقع السعر مشتركة بين المزودين — مصدر واحد لا نسختان.
import { computeAskFairValueGap, describeGap } from '@data/valuation/afg-engine';
import type {
  AlertEntry,
  CityName,
  Comparable,
  DataQuality,
  DealRecord,
  DistrictDetail,
  DistrictSuggestion,
  MapMetrics,
  MapMode,
  MarketPulse,
  Opportunity,
  PricePoint,
  PropertyCategoryAvailability,
  ReportListItem,
  ReportPreview,
  ReportType,
  ScoreComponent,
  ValuationRequest,
  ValuationResult,
  VerdictLevel,
  WatchAlertConditions,
  WatchItem,
} from '@/domain/types';
import {
  DEMO_CITIES,
  DEMO_DISTRICTS,
  demoDistrictById,
  demoDistrictByName,
  demoRandom,
  type DemoDistrictRow,
} from './districts';

/**
 * سيناريوهات العرض — أداة مراجعة فقط.
 * تسمح بمعاينة حالات الواجهة (ثقة منخفضة / بيانات جزئية / لا بيانات / خطأ)
 * دون انتظار ظرف حقيقي. تُحذف مع Provider التجريبي نفسه.
 */
export type DemoScenario = 'normal' | 'low-confidence' | 'partial' | 'no-data' | 'error';

export interface DemoProviderOptions {
  scenario?: DemoScenario;
  /** تأخير تمثيلي بالمللي ثانية (900ms في النموذج المعتمد). */
  latencyMs?: number;
  /** تعطيل التأخير في الاختبارات. */
  instant?: boolean;
}

const MODE_META: Record<MapMode, { label: string; unit: string; field: keyof DemoDistrictRow }> = {
  price: { label: 'السعر', unit: 'ر.س/م²', field: 'deal' },
  opportunity: { label: 'الفرص', unit: '0–100', field: 'opportunity' },
  momentum: { label: 'النمو', unit: 'سنوي %', field: 'momentum' },
  liquidity: { label: 'السيولة', unit: '0–100', field: 'liquidity' },
  pressure: { label: 'ضغط السوق', unit: 'قوة المشتري', field: 'buyerPower' },
};

const MONTH_LABELS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

function verdictOf(score: number): VerdictLevel {
  if (score >= 82) return 'strong';
  if (score >= 70) return 'good';
  if (score >= 56) return 'fair';
  return 'above-market';
}

function isoDaysAgo(days: number): string {
  const d = new Date(Date.UTC(2026, 7, 10));
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export class DemoMarketDataProvider implements MarketDataProvider {
  readonly origin = 'demo' as const;
  readonly label = 'Demo Market Data Provider (deterministic, non-market data)';

  private scenario: DemoScenario;
  private readonly latencyMs: number;
  private watchlist: WatchItem[];

  constructor(options: DemoProviderOptions = {}) {
    this.scenario = options.scenario ?? 'normal';
    this.latencyMs = options.instant ? 0 : (options.latencyMs ?? 900);
    this.watchlist = this.seedWatchlist();
  }

  setScenario(scenario: DemoScenario): void {
    this.scenario = scenario;
  }

  getScenario(): DemoScenario {
    return this.scenario;
  }

  // -------------------------------------------------------------------------
  // أدوات داخلية
  // -------------------------------------------------------------------------

  private async delay(factor = 1): Promise<void> {
    if (this.latencyMs <= 0) return;
    await new Promise((resolve) => setTimeout(resolve, this.latencyMs * factor));
  }

  private quality(comparables: number): DataQuality {
    const partial = this.scenario === 'partial';
    return {
      origin: 'demo',
      dataAgeDays: partial ? 34 : 6,
      comparablesCount: partial ? 3 : comparables,
      comparablesExpected: 6,
      partial,
    };
  }

  private resolve(params: GetDistrictParams): DemoDistrictRow {
    const row = params.districtId
      ? demoDistrictById(params.districtId)
      : params.name
        ? demoDistrictByName(params.name, params.city)
        : undefined;
    if (!row) {
      throw new ProviderError('not_found', 'الحي غير موجود في بيانات العرض.');
    }
    return row;
  }

  private guardScenario(): void {
    if (this.scenario === 'error') {
      throw new ProviderError('unavailable', 'لم نستطع الوصول إلى بيانات الحي الآن.');
    }
    if (this.scenario === 'no-data') {
      throw new ProviderError('insufficient_data', 'لا نستطيع إعطاء نطاق قيمة موثوق لهذا الحي.');
    }
  }

  // -------------------------------------------------------------------------
  // العقد
  // -------------------------------------------------------------------------

  async getCities(): Promise<CityName[]> {
    return [...DEMO_CITIES];
  }

  /** العرض التجريبي الموسوم DEMO يعرض الفئتين — لا صفقات حقيقية فيه أصلاً. */
  async getPropertyCategories(): Promise<PropertyCategoryAvailability> {
    return { LAND: true, BUILDING: true };
  }

  async searchDistricts(query: string, limit = 6): Promise<DistrictSuggestion[]> {
    const q = query.trim();
    if (!q) return [];
    return DEMO_DISTRICTS.filter((d) => d.name.includes(q) || d.city.includes(q))
      .slice(0, limit)
      .map((d) => ({
        districtId: d.id,
        name: d.name,
        city: d.city,
        dealPricePerSqm: d.deal,
      }));
  }

  async getDistrict(params: GetDistrictParams): Promise<DistrictDetail> {
    await this.delay(0.4);
    this.guardScenario();
    const row = this.resolve(params);
    return this.toDistrictDetail(row);
  }

  private toDistrictDetail(row: DemoDistrictRow): DistrictDetail {
    const series: PricePoint[] = row.series.map((deal, i) => {
      const monthIndex = (7 - (35 - i) + 1200) % 12;
      return {
        t: i,
        label: MONTH_LABELS[monthIndex] ?? '',
        deal,
        ask: Math.round((deal * (1 + row.gap / 100)) / 5) * 5,
      };
    });

    const r = demoRandom(`deals:${row.id}`);
    const recentDeals: DealRecord[] = Array.from({ length: 5 }, (_, i) => {
      const areaSqm = Math.round((400 + r() * 900) / 25) * 25;
      const pricePerSqm = Math.round((row.deal * (0.9 + r() * 0.22)) / 5) * 5;
      return {
        id: `${row.id}|deal|${i}`,
        date: isoDaysAgo(Math.round(4 + r() * 86)),
        areaSqm,
        pricePerSqm,
        totalPrice: areaSqm * pricePerSqm,
        street: `شارع ${Math.round(15 + r() * 15)} م`,
      };
    }).sort((a, b) => (a.date < b.date ? 1 : -1));

    const confidence = this.scenario === 'low-confidence' ? 43 : row.confidence;

    return {
      id: row.id,
      name: row.name,
      city: row.city,
      zone: row.zone,
      lat: row.lat,
      lng: row.lng,
      dealPricePerSqm: row.deal,
      askPricePerSqm: row.ask,
      askDealGapPct: row.gap,
      fairValueLowPerSqm: row.fairLo,
      fairValueHighPerSqm: row.fairHi,
      opportunityScore: row.opportunity,
      confidenceScore: confidence,
      details: {
        deals90d: row.deals90,
        supply: row.supply,
        avgDaysOnMarket: row.dom,
        priceCutRate: row.cutRate,
        liquidityScore: row.liquidity,
        annualGrowth: row.momentum,
      },
      pressure: {
        buyerPower: row.buyerPower,
        statement:
          row.buyerPower >= 60
            ? 'الميزان يميل لصالح المشتري: المعروض أوسع ومدة الإعلان أطول من المعتاد في هذا الحي.'
            : row.buyerPower <= 40
              ? 'الميزان يميل لصالح البائع: الطلب أسرع من المعروض ومدة الإعلان أقصر من المعتاد.'
              : 'السوق متوازن نسبياً في هذا الحي خلال المدة الأخيرة.',
        modelVersion: MODEL_VERSIONS.MP,
      },
      series,
      recentDeals,
      quality: this.quality(6),
    };
  }

  async getMapMetrics(params: GetMapMetricsParams): Promise<MapMetrics> {
    await this.delay(0.3);
    if (this.scenario === 'error') {
      throw new ProviderError('unavailable', 'تعذّر تحميل طبقة الخريطة.');
    }
    const meta = MODE_META[params.mode];
    const rows = DEMO_DISTRICTS.filter((d) => d.city === params.city);
    const values = rows.map((d) => Number(d[meta.field])).sort((a, b) => a - b);
    const q = (p: number): number => values[clamp(Math.round((values.length - 1) * p), 0, values.length - 1)] ?? 0;
    return {
      mode: params.mode,
      modeLabel: meta.label,
      unit: meta.unit,
      legendLow: q(0.06),
      legendHigh: q(0.94),
      cells: rows.map((d) => ({
        districtId: d.id,
        name: d.name,
        city: d.city,
        lat: d.lat,
        lng: d.lng,
        value: Number(d[meta.field]),
      })),
      quality: this.quality(6),
    };
  }

  async getValuation(request: ValuationRequest): Promise<ValuationResult> {
    await this.delay();
    this.guardScenario();

    if (!Number.isFinite(request.areaSqm) || request.areaSqm <= 0) {
      throw new ProviderError('invalid_input', 'المساحة يجب أن تكون رقماً أكبر من صفر.');
    }
    if (!Number.isFinite(request.askingPrice) || request.askingPrice <= 0) {
      throw new ProviderError('invalid_input', 'السعر المطلوب يجب أن يكون رقماً أكبر من صفر.');
    }

    const row = this.resolve({ name: request.district, city: request.city });
    const askPerSqm = Math.round(request.askingPrice / request.areaSqm);
    const fairLow = row.fairLo * request.areaSqm;
    const fairHigh = row.fairHi * request.areaSqm;
    const mid = (row.fairLo + row.fairHi) / 2;
    const differencePct = +(((askPerSqm - mid) / mid) * 100).toFixed(1);

    // ملاحظة: هذه ليست خوارزمية تقييم. هي إسقاط حتمي لبيانات العرض على شكل
    // العقد، حتى تتحقق الواجهة من التعامل مع كل الحقول.
    const priceScore = clamp(Math.round(30 - (differencePct / 100) * 120), 0, 30);
    const marketScore = clamp(Math.round(7.5 + row.momentum * 1.1), 0, 15);
    const liquidityScore = clamp(Math.round((row.liquidity / 100) * 15), 0, 15);
    const riskScore = clamp(Math.round(10 - row.cutRate / 3 - row.dom / 60), 0, 10);
    const locationScore = clamp(Math.round((row.opportunity / 100) * 20), 0, 20);
    const timingScore = clamp(Math.round((row.buyerPower / 100) * 10), 0, 10);
    const opportunity = clamp(
      priceScore + marketScore + liquidityScore + riskScore + locationScore + timingScore,
      0,
      100,
    );

    const confidence = this.scenario === 'low-confidence' ? 43 : this.scenario === 'partial' ? 61 : row.confidence;

    const components: ScoreComponent[] = [
      { key: 'price', label: 'السعر', value: priceScore, max: 30, note: 'موقع سعر المتر من منتصف نطاق القيمة العادلة' },
      { key: 'market', label: 'حركة السوق', value: marketScore, max: 15, note: `النمو السنوي للحي ${row.momentum > 0 ? '+' : ''}${row.momentum}%` },
      { key: 'liquidity', label: 'السيولة', value: liquidityScore, max: 15, note: `${row.deals90} صفقة خلال ٩٠ يوماً` },
      { key: 'risk', label: 'المخاطر', value: riskScore, max: 10, note: `تخفيضات ${row.cutRate}% · متوسط إعلان ${row.dom} يوماً` },
      { key: 'location', label: 'الموقع', value: locationScore, max: 20, note: 'مؤشر فرصة الحي مقارنة بالمدينة' },
      { key: 'timing', label: 'التوقيت', value: timingScore, max: 10, note: `قوة المشتري ${row.buyerPower}/100` },
    ];

    const r = demoRandom(`comps:${row.id}:${request.areaSqm}`);
    const count = this.scenario === 'partial' ? 3 : 6;
    const comparables: Comparable[] = Array.from({ length: count }, (_, i) => {
      const pricePerSqm = Math.round((row.deal * (0.88 + r() * 0.26)) / 5) * 5;
      return {
        id: `${row.id}|cmp|${i}`,
        date: isoDaysAgo(Math.round(6 + r() * 84)),
        areaSqm: Math.round((request.areaSqm * (0.72 + r() * 0.6)) / 25) * 25,
        pricePerSqm,
        deltaPct: +(((pricePerSqm - askPerSqm) / askPerSqm) * 100).toFixed(1),
        distanceLabel: `${(0.3 + r() * 2.4).toFixed(1)} كم`,
      };
    });

    const verdict = verdictOf(opportunity);
    // نفس دلالات AFG-1.0: الموقع من حدّي النطاق، والنسبة من التقدير المركزي.
    const askingPerSqm = request.askingPrice / request.areaSqm;
    const fairPointPerSqm = (fairLow + fairHigh) / 2 / request.areaSqm;
    const headline = describeGap(
      computeAskFairValueGap({
        asking_price_per_sqm: askingPerSqm,
        fair_value_low: fairLow / request.areaSqm,
        fair_value_point: fairPointPerSqm,
        fair_value_high: fairHigh / request.areaSqm,
      }),
    );

    return {
      property_id: `demo:${row.id}:${request.areaSqm}:${request.askingPrice}`,
      city: row.city,
      district: row.name,
      area_sqm: request.areaSqm,
      asking_price: request.askingPrice,
      asking_price_per_sqm: askPerSqm,
      fair_value_low: Math.round(fairLow),
      fair_value_high: Math.round(fairHigh),
      difference_percentage: differencePct,
      opportunity_score: opportunity,
      confidence_score: confidence,
      comparables_count: comparables.length,
      data_age: this.scenario === 'partial' ? 34 : 6,
      valuation_model_version: MODEL_VERSIONS.FV,
      confidence_model_version: MODEL_VERSIONS.CF,
      generated_at: new Date().toISOString(),

      verdict,
      headline,
      confidence_explanation:
        'تعتمد على كثافة البيانات، حداثتها، وتشابه الصفقات المستخدمة.',
      reasons: [
        `سعر المتر المطلوب ${askPerSqm.toLocaleString('en-US')} ر.س/م² مقابل نطاق ${row.fairLo.toLocaleString('en-US')}–${row.fairHi.toLocaleString('en-US')} ر.س/م².`,
        `فجوة العرض/التنفيذ في الحي ${row.gap}% خلال المدة الأخيرة.`,
        `${row.deals90} صفقة مسجّلة خلال ٩٠ يوماً ومتوسط مدة إعلان ${row.dom} يوماً.`,
      ],
      score_components: components,
      comparables,
      district_id: row.id,
      location_mode: request.latitude != null && request.longitude != null ? 'PRECISE' : 'DISTRICT_LEVEL',
      scope_label:
        request.latitude != null && request.longitude != null
          ? 'القيمة العادلة المقدّرة لهذه الأرض'
          : 'تقدير أولي على مستوى الحي',
      scope_basis:
        request.latitude != null && request.longitude != null
          ? 'موقع القطعة محدد، والمقارنات مرشّحة بالمسافة من هذا الموقع.'
          : 'لم يُدخل موقع للقطعة، فالمقارنات على مستوى الحي.',
      scope_warning:
        request.latitude != null && request.longitude != null
          ? null
          : 'هذه النتيجة لا تمثل تقييماً موقعياً دقيقاً للقطعة، وإنما مستوى سعر عام في الحي.',
      location_notice: null,
      quality: this.quality(comparables.length),
    };
  }

  async getOpportunities(params: GetOpportunitiesParams): Promise<Opportunity[]> {
    await this.delay(0.4);
    if (this.scenario === 'error') {
      throw new ProviderError('unavailable', 'تعذّر تحميل الفرص الآن.');
    }
    const cities = params.cities.length ? params.cities : [...DEMO_CITIES];
    const riskCeiling = params.risk === 'low' ? 55 : params.risk === 'medium' ? 75 : 100;
    const horizonBias = params.horizon === '1Y' ? 0.5 : params.horizon === '3Y' ? 1 : 1.4;

    const scored = DEMO_DISTRICTS.filter((d) => cities.includes(d.city))
      // رأس المال يحدّد الحد الأقصى لسعر المتر المعقول لقطعة 600م² افتراضياً
      .filter((d) => d.deal * 600 <= params.capital * 1.25)
      .filter((d) => d.cutRate * 4 <= riskCeiling)
      .map((d) => ({
        row: d,
        score: clamp(Math.round(d.opportunity + d.momentum * horizonBias), 0, 100),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, params.limit ?? 8);

    return scored.map((s, i) => ({
      rank: i + 1,
      districtId: s.row.id,
      district: s.row.name,
      city: s.row.city,
      opportunityScore: s.score,
      reasons: [
        `نمو سنوي ${s.row.momentum > 0 ? '+' : ''}${s.row.momentum}%`,
        `سيولة ${s.row.liquidity}/100`,
        `فجوة عرض/تنفيذ ${s.row.gap}%`,
      ],
      spark: s.row.series.slice(-12),
      dealPricePerSqm: s.row.deal,
      quality: this.quality(6),
    }));
  }

  // -------------------------------------------------------------------------
  // المراقبة
  // -------------------------------------------------------------------------

  private seedWatchlist(): WatchItem[] {
    const seeds = ['الرياض|المونسية', 'الرياض|القادسية', 'جدة|الياقوت'];
    return seeds
      .map((id) => demoDistrictById(id))
      .filter((d): d is DemoDistrictRow => Boolean(d))
      .map((d) => ({
        id: `watch:${d.id}`,
        districtId: d.id,
        district: d.name,
        city: d.city,
        addedAt: isoDaysAgo(12),
        dealPricePerSqm: d.deal,
        changePct: d.momentum,
        opportunityScore: d.opportunity,
        conditions: {
          priceDrop: true,
          newDeals: true,
          supplyChange: false,
          opportunityJump: false,
        },
      }));
  }

  async getWatchlist(): Promise<WatchItem[]> {
    await this.delay(0.2);
    return [...this.watchlist];
  }

  async addToWatchlist(m: WatchlistMutation): Promise<WatchItem[]> {
    const row = demoDistrictById(m.districtId);
    if (!row) throw new ProviderError('not_found', 'الحي غير موجود.');
    if (!this.watchlist.some((w) => w.districtId === row.id)) {
      this.watchlist = [
        ...this.watchlist,
        {
          id: `watch:${row.id}`,
          districtId: row.id,
          district: row.name,
          city: row.city,
          addedAt: isoDaysAgo(0),
          dealPricePerSqm: row.deal,
          changePct: row.momentum,
          opportunityScore: row.opportunity,
          conditions: {
            priceDrop: true,
            newDeals: true,
            supplyChange: false,
            opportunityJump: false,
            ...m.conditions,
          },
        },
      ];
    }
    return [...this.watchlist];
  }

  async removeFromWatchlist(districtId: string): Promise<WatchItem[]> {
    this.watchlist = this.watchlist.filter((w) => w.districtId !== districtId);
    return [...this.watchlist];
  }

  async updateWatchConditions(
    districtId: string,
    conditions: WatchAlertConditions,
  ): Promise<WatchItem[]> {
    this.watchlist = this.watchlist.map((w) =>
      w.districtId === districtId ? { ...w, conditions } : w,
    );
    return [...this.watchlist];
  }

  // -------------------------------------------------------------------------
  // نبض السوق والتقارير
  // -------------------------------------------------------------------------

  async getMarketPulse(city: CityName = 'الرياض'): Promise<MarketPulse> {
    await this.delay(0.25);
    const rows = DEMO_DISTRICTS.filter((d) => d.city === city);
    const avg = (fn: (d: DemoDistrictRow) => number): number =>
      rows.reduce((s, d) => s + fn(d), 0) / (rows.length || 1);

    const alerts: AlertEntry[] = [
      { id: 'a1', level: 'positive', text: 'ارتفاع عدد الصفقات في المونسية خلال آخر أسبوعين.', at: 'قبل ٢٤ دقيقة' },
      { id: 'a2', level: 'warning', text: 'ارتفاع نسبة تخفيضات الأسعار في العليا.', at: 'قبل ساعتين' },
      { id: 'a3', level: 'info', text: 'تحديث بيانات ٩٠ يوماً لأحياء شمال الرياض.', at: 'اليوم' },
      { id: 'a4', level: 'positive', text: 'اتساع فجوة العرض/التنفيذ في القادسية.', at: 'أمس' },
    ];

    return {
      rows: [
        { label: 'متوسط سعر التنفيذ', value: `${Math.round(avg((d) => d.deal)).toLocaleString('en-US')} ر.س/م²`, tone: 'neutral' },
        { label: 'فجوة العرض/التنفيذ', value: `${avg((d) => d.gap).toFixed(1)}%`, tone: 'warn' },
        { label: 'النمو السنوي', value: `${avg((d) => d.momentum) > 0 ? '+' : ''}${avg((d) => d.momentum).toFixed(1)}%`, tone: 'up' },
        { label: 'متوسط مدة الإعلان', value: `${Math.round(avg((d) => d.dom))} يوماً`, tone: 'neutral' },
      ],
      alerts,
      quality: this.quality(6),
    };
  }

  async listReports(): Promise<ReportListItem[]> {
    await this.delay(0.2);
    return [
      { id: 'rep-1', type: 'district', title: 'تقرير حي — المونسية', createdAt: isoDaysAgo(2) },
      { id: 'rep-2', type: 'valuation', title: 'تقرير تقييم — قطعة في القادسية', createdAt: isoDaysAgo(9) },
      { id: 'rep-3', type: 'portfolio', title: 'تقرير محفظة — قائمة المراقبة', createdAt: isoDaysAgo(21) },
    ];
  }

  async getReport(type: ReportType, subjectId?: string): Promise<ReportPreview> {
    await this.delay(0.3);
    if (this.scenario === 'error') {
      throw new ProviderError('unavailable', 'تعذّر توليد معاينة التقرير.');
    }
    const row = demoDistrictById(subjectId ?? 'الرياض|المونسية') ?? DEMO_DISTRICTS[0]!;
    const base = {
      generatedAt: new Date().toISOString(),
      quality: this.quality(6),
      limitations: [
        'بيانات تجريبية — لا يستخدم هذا التقرير بيانات سوق فعلية.',
        'نطاق القيمة العادلة تقدير إحصائي وليس تثميناً معتمداً.',
        'الأرقام لا تُستخدم كتوصية استثمارية.',
      ],
    };

    if (type === 'district') {
      return {
        ...base,
        id: `report:district:${row.id}`,
        type,
        title: `تقرير حي — ${row.name}`,
        subtitle: `${row.city} · ${row.zone}`,
        kpis: [
          { label: 'متوسط سعر التنفيذ', value: row.deal.toLocaleString('en-US'), unit: 'ر.س/م²' },
          { label: 'متوسط سعر العرض', value: row.ask.toLocaleString('en-US'), unit: 'ر.س/م²' },
          { label: 'فجوة العرض/التنفيذ', value: `${row.gap}%` },
          { label: 'مؤشر الفرصة', value: `${row.opportunity} / 100` },
        ],
        sections: [
          { title: 'ملخص السوق', body: `سجّل الحي ${row.deals90} صفقة خلال ٩٠ يوماً، بمتوسط مدة إعلان ${row.dom} يوماً وحجم معروض ${row.supply} إعلاناً.` },
          { title: 'السعر', body: `متوسط سعر التنفيذ ${row.deal.toLocaleString('en-US')} ر.س/م²، ونطاق القيمة العادلة ${row.fairLo.toLocaleString('en-US')}–${row.fairHi.toLocaleString('en-US')} ر.س/م².` },
          { title: 'ضغط السوق', body: `قوة المشتري ${row.buyerPower}/100 ومؤشر السيولة ${row.liquidity}/100.` },
          { title: 'المخاطر', body: `نسبة تخفيضات الأسعار ${row.cutRate}%، وهي المؤشر الأهم لمتابعة تحوّل الاتجاه.` },
        ],
      };
    }

    if (type === 'valuation') {
      return {
        ...base,
        id: `report:valuation:${row.id}`,
        type,
        title: `تقرير تقييم — قطعة في ${row.name}`,
        subtitle: `${row.city} · مساحة 750 م²`,
        kpis: [
          { label: 'السعر المطلوب', value: (row.ask * 750).toLocaleString('en-US'), unit: 'ر.س' },
          { label: 'نطاق القيمة العادلة', value: `${(row.fairLo * 750).toLocaleString('en-US')}–${(row.fairHi * 750).toLocaleString('en-US')}`, unit: 'ر.س' },
          { label: 'مؤشر الفرصة', value: `${row.opportunity} / 100` },
          { label: 'الثقة في التقييم', value: `${row.confidence}%` },
        ],
        sections: [
          { title: 'الحكم', body: 'السعر المطلوب مقارَن بنطاق القيمة العادلة المحسوب من صفقات الحي خلال ٩٠ يوماً.' },
          { title: 'الصفقات المماثلة', body: 'استُخدمت ٦ صفقات ضمن نطاق مساحي وزمني متقارب.' },
          { title: 'حركة السوق', body: `النمو السنوي ${row.momentum > 0 ? '+' : ''}${row.momentum}% ومؤشر السيولة ${row.liquidity}/100.` },
          { title: 'حدود التقرير', body: 'بيانات تجريبية — لا يستخدم هذا التقرير بيانات سوق فعلية.' },
        ],
      };
    }

    return {
      ...base,
      id: 'report:portfolio',
      type,
      title: 'تقرير محفظة — قائمة المراقبة',
      subtitle: `${this.watchlist.length} عنصر تحت المراقبة`,
      kpis: [
        { label: 'عناصر المراقبة', value: String(this.watchlist.length) },
        { label: 'متوسط مؤشر الفرصة', value: String(Math.round(this.watchlist.reduce((s, w) => s + w.opportunityScore, 0) / (this.watchlist.length || 1))) },
        { label: 'أعلى نمو', value: `${Math.max(0, ...this.watchlist.map((w) => w.changePct)).toFixed(1)}%` },
        { label: 'مدن مغطاة', value: String(new Set(this.watchlist.map((w) => w.city)).size) },
      ],
      sections: [
        { title: 'نظرة عامة', body: 'ملخص لحالة الأحياء التي تراقبها ومؤشراتها الأساسية.' },
        { title: 'التغيّرات', body: 'أبرز تغيّرات سعر التنفيذ وحجم المعروض منذ إضافة كل عنصر.' },
        { title: 'التنبيهات', body: 'شروط التنبيه المفعّلة لكل عنصر وعدد المرات التي تحقّقت فيها.' },
        { title: 'حدود التقرير', body: 'بيانات تجريبية — لا يستخدم هذا التقرير بيانات سوق فعلية.' },
      ],
    };
  }
}
